import { stripOptionPrefix } from "@/utils/text-format"

type DocxModule = typeof import("docx")

let docxModulePromise: Promise<DocxModule> | null = null

async function loadDocx(): Promise<DocxModule> {
  if (!docxModulePromise) {
    docxModulePromise = import("docx")
  }
  return docxModulePromise
}

const TWIP_PER_INCH = 1440

export interface QuizExportQuestion {
  order: number
  questionText: string
  options: string[]
  correctAnswerIndex?: number | null
  rationale?: string | null
}

export interface QuizExportData {
  id: string
  name: string
  description?: string | null
  questions: QuizExportQuestion[]
}

export interface ExportOptions {
  includeAnswers: boolean
  includeRationales: boolean
  twoColumns?: boolean
}

export async function exportQuizToDocx(
  quiz: QuizExportData,
  opts: ExportOptions,
): Promise<Uint8Array> {
  const { Packer } = await loadDocx()
  const doc = await buildDocument(quiz, opts)
  const blob = await Packer.toBlob(doc)
  const arrayBuffer = await blob.arrayBuffer()
  return new Uint8Array(arrayBuffer)
}

export async function downloadQuizDocx(
  quiz: QuizExportData,
  opts: ExportOptions,
  filename?: string,
): Promise<void> {
  const { Packer } = await loadDocx()
  const doc = await buildDocument(quiz, opts)
  const blob = await Packer.toBlob(doc)
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = ensureDocxExtension(
    filename ?? `${quiz.name || "quiz"}.docx`,
  )
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

function stripHtml(value?: string | null): string {
  if (!value) return ""
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

function ensureDocxExtension(name: string): string {
  return name.toLowerCase().endsWith(".docx") ? name : `${name}.docx`
}

async function buildDocument(quiz: QuizExportData, opts: ExportOptions) {
  const { AlignmentType, Document, HeadingLevel, Paragraph, TextRun } =
    await loadDocx()
  const { includeAnswers, includeRationales, twoColumns } = opts

  const children: any[] = []

  children.push(
    new Paragraph({
      text: quiz.name,
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
    }),
  )

  if (quiz.description) {
    children.push(
      new Paragraph({
        text: quiz.description,
        alignment: AlignmentType.CENTER,
      }),
    )
  }

  const sortedQuestions = [...quiz.questions].sort((a, b) => a.order - b.order)
  for (const question of sortedQuestions) {
    children.push(
      new Paragraph({
        spacing: { before: 240 },
        children: [
          new TextRun({
            text: `${question.order}. ${stripHtml(question.questionText)}`,
            bold: true,
          }),
        ],
      }),
    )

    for (const [index, optionText] of question.options.entries()) {
      const letter = String.fromCharCode(65 + index)
      children.push(
        new Paragraph({
          text: `${letter}. ${stripOptionPrefix(stripHtml(optionText))}`,
          indent: { left: 720 },
        }),
      )
    }

    if (includeAnswers && typeof question.correctAnswerIndex === "number") {
      const letter = String.fromCharCode(65 + question.correctAnswerIndex)
      children.push(
        new Paragraph({
          spacing: { before: 120 },
          indent: { left: 720 },
          children: [
            new TextRun({ text: "Correct Answer: ", bold: true }),
            new TextRun({ text: letter }),
          ],
        }),
      )
    }

    if (includeRationales && question.rationale) {
      children.push(
        new Paragraph({
          indent: { left: 720 },
          children: [
            new TextRun({ text: "Rationale: ", bold: true }),
            new TextRun({
              text: stripHtml(question.rationale),
              italics: true,
            }),
          ],
        }),
      )
    }
  }

  return new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: TWIP_PER_INCH,
              bottom: TWIP_PER_INCH,
              left: TWIP_PER_INCH,
              right: TWIP_PER_INCH,
            },
          },
          ...(twoColumns && {
            column: {
              count: 2,
              space: TWIP_PER_INCH / 2, // 0.5 inch gap between columns
            },
          }),
        },
        children,
      },
    ],
  })
}
